/* eslint-disable react/prop-types */
import { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Button from "@mui/material/Button";
import { numberToWords } from "../components/printTemplate";

const createItem = (orderNumber, itemName) => ({
  orderNumber,
  itemName,
  rateOrPeriod: "",
  fixedAssetCost: "",
  depreciationAmount: "",
  workshop1: "",
  workshop2: "",
  workshop3: "",
  workshop4: "",
  tk623ConstructionMachine: "",
  tk641SellingExpense: "",
  tk642AdministrativeExpense: "",
  tk241ConstructionInProgress: "",
  tk242DeferredExpense: "",
  tk335AccruedExpense: "",
  tkOther: "",
});

const defaultItems = [
  createItem("1", "I. Số khấu hao trích tháng trước"),
  createItem("2", "II. Số KH TSCĐ tăng trong tháng"),
  createItem("3", "III. Số KH TSCĐ giảm trong tháng"),
];

function DepreciationTable({ formData }) {
  const { values = {}, onChangeValue } = formData || {};
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [headerInfo, setHeaderInfo] = useState({
    unit: values.unit || "",
    department: values.department || "",
    documentNumber: values.documentNumber || "",
    month: values.month || currentMonth,
    year: values.year || currentYear,
    signingDate: values.signingDate || today.toISOString().split("T")[0],
    preparerName: values.preparerName || "",
    accountantName: values.accountantName || "",
  });

  const [localItems, setLocalItems] = useState(() => {
    if (values.items && Array.isArray(values.items) && values.items.length > 0) {
      return values.items;
    }
    return defaultItems;
  });

  const [localTotal, setLocalTotal] = useState(() => {
    if (values.total) {
      return values.total;
    }
    return createItem("", "IV. Số KH trích tháng này (I + II - III)");
  });

  const handleHeaderChange = (field, val) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: val }));
    if (onChangeValue) {
      onChangeValue(field, val);
    }
  };

  useEffect(() => {
    if (values.items && values.items.length > 0) {
      setLocalItems(values.items);
      setHeaderInfo({
        unit: values.unit || "",
        department: values.department || "",
        documentNumber: values.documentNumber || "",
        month: values.month || currentMonth,
        year: values.year || currentYear,
        signingDate: values.signingDate || today.toISOString().split("T")[0],
        preparerName: values.preparerName || "",
        accountantName: values.accountantName || "",
      });
    }
    if (values.total) {
      setLocalTotal(values.total);
    }
  }, [
    values.items,
    values.total,
    values.unit,
    values.department,
    values.documentNumber,
    values.month,
    values.year,
    values.signingDate,
    values.preparerName,
    values.accountantName,
  ]);

  const handleChangeItem = (index, field, value) => {
    const newItems = [...localItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setLocalItems(newItems);
  };

  // Calculate totals (row IV = row I + row II - row III)
  const calculatedTotal = useMemo(() => {
    const num = (val) => (val && val !== "" && !isNaN(val) ? parseFloat(val) : 0);

    const fields = [
      "fixedAssetCost",
      "depreciationAmount",
      "workshop1",
      "workshop2",
      "workshop3",
      "workshop4",
      "tk623ConstructionMachine",
      "tk641SellingExpense",
      "tk642AdministrativeExpense",
      "tk241ConstructionInProgress",
      "tk242DeferredExpense",
      "tk335AccruedExpense",
    ];

    const total = { ...localTotal };

    fields.forEach((field) => {
      total[field] =
        num(localItems[0][field]) + num(localItems[1][field]) - num(localItems[2][field]);
    });

    return total;
  }, [localItems, localTotal]);

  useEffect(() => {
    if (onChangeValue) {
      onChangeValue("items", localItems);
      onChangeValue("total", calculatedTotal);
    }
  }, [localItems, calculatedTotal]);

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "14px",
    },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "200px",
      fontSize: "14px",
      fontFamily: "inherit",
      fontWeight: "bold",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    inputDottedSmall: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      width: "50px",
      fontSize: "14px",
      fontFamily: "inherit",
      textAlign: "center",
      backgroundColor: "transparent",
      display: "inline-block",
      color: "#000",
    },
    titleBox: { textAlign: "center", margin: "20px 0" },
    titleMain: { fontSize: "20px", fontWeight: "bold", margin: 0, textTransform: "uppercase" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "12px" },
    th: {
      border: "1px solid #000",
      padding: "4px",
      textAlign: "center",
      fontWeight: "bold",
      verticalAlign: "middle",
      height: "40px",
    },
    td: { border: "1px solid #000", padding: 0, height: "30px" },
    inputTable: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    inputTableText: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: "13px",
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 4px",
      display: "block",
      color: "#000",
    },
    totalText: {
      padding: "0 4px",
      textAlign: "right",
      fontWeight: "bold",
      display: "block",
      lineHeight: "30px",
    },
    calcRowBg: { backgroundColor: "#fdfdfd", fontWeight: "bold" },
    totalRowBg: { backgroundColor: "#eee", fontWeight: "bold", textAlign: "center" },
  };

  const fmt = (n) => (n ? new Intl.NumberFormat("vi-VN").format(n) : "");

  const handleMonthChange = (val) => {
    if (val === "" || /^[0-9\b]+$/.test(val)) handleHeaderChange("month", val);
  };

  const handleYearChange = (val) => {
    if (val === "" || /^[0-9\b]+$/.test(val)) {
      if (val.length <= 4) handleHeaderChange("year", val);
    }
  };

  // Combine items with calculated total for display
  const displayItems = [...localItems, { ...calculatedTotal, isTotal: true }];

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "50%" }}>
          <div style={{ fontWeight: "bold", marginBottom: 5 }}>
            Đơn vị:{" "}
            <input
              disabled={containView}
              style={s.inputDotted}
              value={headerInfo.unit}
              onChange={(e) => handleHeaderChange("unit", e.target.value)}
            />
          </div>
          <div style={{ fontWeight: "bold" }}>
            Bộ phận:{" "}
            <input
              disabled={containView}
              style={s.inputDotted}
              value={headerInfo.department}
              onChange={(e) => handleHeaderChange("department", e.target.value)}
            />
          </div>
        </div>
        <div style={{ textAlign: "center", width: "50%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số 06 - TSCĐ</div>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginTop: 4 }}>
            (Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ
            Tài chính)
          </div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={s.titleMain}>BẢNG TÍNH VÀ PHÂN BỔ KHẤU HAO TSCĐ</h2>
        <div style={{ fontStyle: "italic", marginTop: 5, fontSize: "16px" }}>
          Tháng{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "30px" }}
            value={headerInfo.month}
            onChange={(e) => handleMonthChange(e.target.value)}
            placeholder="MM"
          />{" "}
          năm{" "}
          <input
            disabled={containView}
            style={{ ...s.inputDottedSmall, width: "50px" }}
            value={headerInfo.year}
            onChange={(e) => handleYearChange(e.target.value)}
            placeholder="YYYY"
          />
        </div>
        <div style={{ textAlign: "right", marginTop: 10 }}>
          Số:{" "}
          <input
            disabled={containView}
            style={s.inputDottedSmall}
            value={headerInfo.documentNumber}
            onChange={(e) => handleHeaderChange("documentNumber", e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th rowSpan={2} style={s.th}>
                STT
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "180px" }}>
                Chỉ tiêu
              </th>
              <th rowSpan={2} style={{ ...s.th, width: "80px" }}>
                Tỷ lệ <br />
                khấu hao <br />
                (%) hoặc
                <br />
                thời gian
                <br />
                sử dụng
              </th>
              <th colSpan={2} style={s.th}>
                Toàn DN
              </th>
              <th colSpan={4} style={s.th}>
                TK 627-Chi phí sản xuất chung
              </th>
              <th rowSpan={2} style={s.th}>
                TK 623 <br />
                Chi phí <br />
                sử dụng <br />
                máy thi <br />
                công
              </th>
              <th rowSpan={2} style={s.th}>
                TK 641 <br />
                Chi phí <br />
                bán <br />
                hàng
              </th>
              <th rowSpan={2} style={s.th}>
                TK 642 <br />
                Chi phí <br />
                quản lý <br />
                doanh <br />
                nghiệp
              </th>
              <th rowSpan={2} style={s.th}>
                TK 241 <br />
                XDCB <br />
                dở <br />
                dang
              </th>
              <th rowSpan={2} style={s.th}>
                TK 242 <br />
                Chi phí <br />
                chờ <br />
                phân bổ
              </th>
              <th rowSpan={2} style={s.th}>
                TK 335 <br />
                Chi phí <br />
                phải trả
              </th>
              <th rowSpan={2} style={s.th}>
                ...
              </th>
            </tr>
            <tr>
              <th style={s.th}>
                Nguyên <br />
                giá TSCĐ
              </th>
              <th style={s.th}>
                Số khấu <br />
                hao
              </th>
              <th style={s.th}>
                Phân <br />
                xưởng 1
              </th>
              <th style={s.th}>
                Phân <br />
                xưởng 2
              </th>
              <th style={s.th}>
                Phân <br />
                xưởng 3
              </th>
              <th style={s.th}>
                Phân <br />
                xưởng 4
              </th>
            </tr>
            <tr style={{ backgroundColor: "#fafafa" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              <th style={s.th}>6</th>
              <th style={s.th}>7</th>
              <th style={s.th}>8</th>
              <th style={s.th}>9</th>
              <th style={s.th}>10</th>
              <th style={s.th}>11</th>
              <th style={s.th}>12</th>
              <th style={s.th}>13</th>
              <th style={s.th}>...</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((row, i) => (
              <tr key={i} style={row.isTotal ? s.calcRowBg : {}}>
                <td
                  style={{
                    ...s.td,
                    textAlign: "center",
                    fontWeight: row.isTotal ? "bold" : "normal",
                  }}
                >
                  {row.orderNumber || ""}
                </td>
                <td
                  style={{
                    ...s.td,
                    paddingLeft: "5px",
                    fontWeight: row.isTotal ? "bold" : "normal",
                  }}
                >
                  {row.itemName || ""}
                </td>
                <td style={s.td}>
                  <input
                    disabled={containView || row.isTotal}
                    style={{ ...s.inputTable, textAlign: "center" }}
                    value={row.rateOrPeriod || ""}
                    onChange={(e) => handleChangeItem(i, "rateOrPeriod", e.target.value)}
                  />
                </td>

                {/* Columns 2 to 13+ */}
                {[
                  "fixedAssetCost",
                  "depreciationAmount",
                  "workshop1",
                  "workshop2",
                  "workshop3",
                  "workshop4",
                  "tk623ConstructionMachine",
                  "tk641SellingExpense",
                  "tk642AdministrativeExpense",
                  "tk241ConstructionInProgress",
                  "tk242DeferredExpense",
                  "tk335AccruedExpense",
                ].map((field) => (
                  <td key={field} style={s.td}>
                    {row.isTotal ? (
                      <span style={s.totalText}>{fmt(row[field] || 0)}</span>
                    ) : (
                      <input
                        disabled={containView}
                        type="number"
                        style={s.inputTable}
                        value={row[field] || ""}
                        onChange={(e) => handleChangeItem(i, field, e.target.value)}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}

            {/* Total Row */}
            <tr style={s.totalRowBg}>
              <td colSpan={2} style={s.td}>
                Cộng
              </td>
              <td style={s.td}>x</td>
              {[
                "fixedAssetCost",
                "depreciationAmount",
                "workshop1",
                "workshop2",
                "workshop3",
                "workshop4",
                "tk623ConstructionMachine",
                "tk641SellingExpense",
                "tk642AdministrativeExpense",
                "tk241ConstructionInProgress",
                "tk242DeferredExpense",
                "tk335AccruedExpense",
              ].map((field) => (
                <td key={field} style={s.td}>
                  <span style={s.totalText}>{fmt(calculatedTotal[field] || 0)}</span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <div style={{ width: "50%" }}>
          <b>Người lập bảng</b>
          <br />
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.preparerName}
            onChange={(e) => handleHeaderChange("preparerName", e.target.value)}
            placeholder="Ký, họ tên"
          />
        </div>
        <div style={{ width: "50%" }}>
          <div style={{ fontStyle: "italic", marginBottom: 5 }}>
            Ngày {headerInfo.signingDate ? new Date(headerInfo.signingDate).getDate() : ""} tháng{" "}
            {headerInfo.signingDate ? new Date(headerInfo.signingDate).getMonth() + 1 : ""} năm{" "}
            {headerInfo.signingDate ? new Date(headerInfo.signingDate).getFullYear() : ""}
          </div>
          <b>Kế toán trưởng</b>
          <br />
          <input
            disabled={containView}
            style={{ ...s.inputDotted, width: "150px", textAlign: "center" }}
            value={headerInfo.accountantName}
            onChange={(e) => handleHeaderChange("accountantName", e.target.value)}
            placeholder="Ký, họ tên"
          />
        </div>
      </div>

      <div style={{ marginTop: "30px", fontSize: "14px" }}>
        <b>Ghi chú:</b> <i style={{ textDecoration: "underline" }}>Tùy</i>{" "}
        <i style={{ fontStyle: "italic" }}>
          theo đặc điểm hoạt động sản xuất kinh doanh và yêu cầu quản lý của đơn vị mình, doanh
          nghiệp được xây dựng, thiết kế biểu mẫu chứng từ kế toán.
        </i>
      </div>
    </SoftBox>
  );
}

DepreciationTable.propTypes = {
  formData: PropTypes.object,
};

export default DepreciationTable;
